import { createReducer, Action, on} from '@ngrx/store';
import { createEntityAdapter, Dictionary, EntityAdapter } from '@ngrx/entity';
import * as fromActions from './channel-details.actions';

import { Channel, ChannelDetails, News } from '@app/core/models/newsboard';
import { Pagination } from '@app/core/models';
import { INITIAL_CHANNEL_ALL_NEWS_FILTERS, INITIAL_CHANNEL_ARCHIVED_NEWS_FILTERS, INITIAL_CHANNEL_NEWS_SUGGESTION_FILTERS, NewsTableFilters, TableState } from '@app/shared/filters/filters-model';

export const feature = 'details';



export const adapter: EntityAdapter<Partial<News>> =
 createEntityAdapter<Partial<News>>({
  selectId: (e) => e.id
 });

export interface State {
  details: ChannelDetails;
  newsPreviewDetails: News;
  allNews: TableState<Partial<News>, NewsTableFilters>;
  newsSuggestions: TableState<Partial<News>, NewsTableFilters>;
  archivedNews: TableState<Partial<News>, NewsTableFilters>;
}

export const initialState: State = {
  details: null,
  newsPreviewDetails: null,
  allNews: {
    data: adapter.getInitialState(),
    pagination: {
      ...Pagination,
      pageIndex: 0,
      pageSize: 10,
    },
    sort: {
      active: 'createdDate',
      direction: 'desc'
    },
    defaultSort: {
      active: 'createdDate',
      direction: 'desc'
    },
    filters: INITIAL_CHANNEL_ALL_NEWS_FILTERS,
    defaultFilters: INITIAL_CHANNEL_ALL_NEWS_FILTERS,
    isLoading: false,
    resetIndex: false,
  },
  newsSuggestions: {
    data: adapter.getInitialState(),
    pagination: {
      ...Pagination,
      pageIndex: 0,
      pageSize: 10,
    },
    sort: {
      active: 'suggestedDate',
      direction: 'desc'
    },
    defaultSort: {
      active: 'suggestedDate',
      direction: 'desc'
    },
    filters: INITIAL_CHANNEL_NEWS_SUGGESTION_FILTERS,
    defaultFilters: INITIAL_CHANNEL_NEWS_SUGGESTION_FILTERS,
    isLoading: false,
    resetIndex: false,
  },
  archivedNews: {
    data: adapter.getInitialState(),
    pagination: {
      ...Pagination,
      pageIndex: 0,
      pageSize: 10,
    },
    sort: {
      active: 'modifiedDate',
      direction: 'desc'
    },
    defaultSort: {
      active: 'modifiedDate',
      direction: 'desc'
    },
    filters: INITIAL_CHANNEL_ARCHIVED_NEWS_FILTERS,
    defaultFilters: INITIAL_CHANNEL_ARCHIVED_NEWS_FILTERS,
    isLoading: false,
    resetIndex: false,
  },
};

const tableReducer = createReducer(
  initialState,
  on(fromActions.setChannelDetails, (state, {details}) => ({
    ...state,
    details
  })),
  
  on(fromActions.getAllNewsRequest, (state) => ({
    ...state,
    allNews: {
      ...state.allNews,
      isLoading: true
    }
  })),
  on(fromActions.getAllNewsSuccess, fromActions.getAllNewsFailure, (state) => ({
    ...state,
    allNews: {
      ...state.allNews,
      isLoading: false
    }
  })),
  on(fromActions.getAllNewsSuccess, (state, {data, pagination}) => ({
    ...state,
    allNews: {
      ...state.allNews,
      data: adapter.setAll(data, state.allNews.data),
      pagination,
      resetIndex: false,
    }
  })),
  on(fromActions.removeAllNewsSuccess, (state) => ({
    ...state,
    allNews: {
      ...state.allNews,
      resetIndex: true
    }
  })),
  on(fromActions.changeAllNewsFilters, (state, {filters}) => ({
    ...state,
    allNews: {
      ...state.allNews,
      filters,
      resetIndex: true
    }
  })),
  on(fromActions.changeAllNewsSort, (state, {active, direction}) => ({
    ...state,
    allNews: {
      ...state.allNews,
      sort: {
        active,
        direction
      }
    }
  })),
  on(fromActions.changeAllNewsPagination, (state, {pageIndex, pageSize}) => ({
    ...state,
    allNews: {
      ...state.allNews,
      pagination: {
        ...state.allNews.pagination,
        pageIndex,
        pageSize
      }
    }
  })),
  on(fromActions.getNewsSuggestionsRequest, (state) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      isLoading: true
    }
  })),
  on(fromActions.getNewsSuggestionsSuccess, fromActions.getNewsSuggestionsFailure, (state) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      isLoading: false
    }
  })),
  on(fromActions.getNewsSuggestionsSuccess, (state, {data, pagination}) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      data: adapter.setAll(data, state.newsSuggestions.data),
      pagination,
      resetIndex: false
    }
  })),
  on(fromActions.changeNewsSuggestionsFilters, (state, {filters}) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      filters,
      resetIndex: true
    }
  })),
  on(fromActions.changeNewsSuggestionsSort, (state, {active, direction}) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      sort: {
        active,
        direction
      }
    }
  })),
  on(fromActions.changeNewsSuggestionsPagination, (state, {pageIndex, pageSize}) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      pagination: {
        ...state.newsSuggestions.pagination,
        pageIndex,
        pageSize
      }
    }
  })),

  
  on(fromActions.getArchivedNewsRequest, (state) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      isLoading: true
    }
  })),
  on(fromActions.getArchivedNewsSuccess, fromActions.getArchivedNewsFailure, (state) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      isLoading: false
    }
  })),
  on(fromActions.getArchivedNewsSuccess, (state, {data, pagination}) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      data: adapter.setAll(data, state.archivedNews.data),
      pagination,
      resetIndex: false
    }
  })),
  on(fromActions.removeArchivedNewsSuccess, (state) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      resetIndex: true
    }
  })),
  on(fromActions.changeArchivedNewsFilters, (state, {filters}) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      filters,
      resetIndex: true
    }
  })),
  on(fromActions.changeArchivedNewsSort, (state, {active, direction}) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      sort: {
        active,
        direction
      }
    }
  })),
  on(fromActions.changeArchivedNewsPagination, (state, {pageIndex, pageSize}) => ({
    ...state,
    archivedNews: {
      ...state.archivedNews,
      pagination: {
        ...state.archivedNews.pagination,
        pageIndex,
        pageSize
      }
    }
  })),
  on(fromActions.rejectNewsSuccess, (state) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      resetIndex: true
    }
  })),
  on(fromActions.acceptNewsSuccess, (state) => ({
    ...state,
    newsSuggestions: {
      ...state.newsSuggestions,
      resetIndex: true
    },
    allNews: {
      ...state.allNews,
      resetIndex: true
    }
  })),
  on(fromActions.clearAll, (state) => ({
    ...initialState
  })),
  on(fromActions.pinNewsSuccess, (state) => ({
    ...state,
    allNews: {
      ...state.allNews,
      resetIndex: true
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return tableReducer(state, action);
}

export const getDetails = (state: State) => state.details;
export const getChannelId = (details: ChannelDetails) => details?.id;
export const getRoleInChannel = (details: ChannelDetails) => details?.role;

export const getAllNews = (state: State) => state.allNews;
export const getNewsSuggestions = (state: State) => state.newsSuggestions;
export const getArchivedNews = (state: State) => state.archivedNews;

export const getPagination = (table: TableState<Partial<News>, NewsTableFilters>) => table.pagination;
export const getSort = (table: TableState<Partial<News>, NewsTableFilters>) => table.sort;
export const getDefaultSort = (table: TableState<Partial<News>, NewsTableFilters>) => table.defaultSort;
export const getFilters = (table: TableState<Partial<News>, NewsTableFilters>) => table.filters;
export const getDefaultFilters = (table: TableState<Partial<News>, NewsTableFilters>) => table.defaultFilters;
export const getIsLoading = (table: TableState<Partial<News>, NewsTableFilters>) => table.isLoading;

export const getRequestData = ({pagination, sort, filters, resetIndex}: TableState<Partial<News>, NewsTableFilters>) => ({
  pageIndex: pagination.pageIndex,
  pageSize: pagination.pageSize,
  sort,
  filters,
  resetIndex});

export const getTableData = (table: TableState<Partial<News>, NewsTableFilters>) => adapter.getSelectors().selectAll(table.data);

// Pagination fields
export const getPageSize = (pagination: Pagination) => pagination.pageSize;
export const getPageIndex = (pagination: Pagination) => pagination.pageIndex;
export const getTotalCount = (pagination: Pagination) => pagination.totalCount;

// Filters fields
export const getSearch = (filters: NewsTableFilters) => filters.search.value;

export const getEntitiesByIds = (entities: Dictionary<Channel>, ids: string[]): Channel[] => ids.map(id => entities[id]);
